import type { Rarity } from '@anidex/shared/economy';
import { primeAmbient, setAmbientEnabled } from './ambient';

const MASTER_GAIN = 0.06;

type Tone = {
  frequency: number;
  duration: number;
  type?: OscillatorType;
  gain?: number;
  delay?: number;
  attack?: number;
  release?: number;
};

let context: AudioContext | null = null;
let master: GainNode | null = null;
let unlocked = false;
let enabled = true;

function ensureContext(): AudioContext | null {
  if (typeof window === 'undefined') return null;
  if (!context) {
    const Ctor = window.AudioContext || (window as unknown as { webkitAudioContext?: typeof AudioContext }).webkitAudioContext;
    if (!Ctor) return null;
    context = new Ctor();
    master = context.createGain();
    master.gain.value = enabled ? MASTER_GAIN : 0;
    master.connect(context.destination);
  }
  return context;
}

async function resume(): Promise<AudioContext | null> {
  const ctx = ensureContext();
  if (!ctx) return null;
  if (ctx.state === 'suspended') {
    try {
      await ctx.resume();
    } catch {
      return null;
    }
  }
  unlocked = ctx.state === 'running';
  return unlocked ? ctx : null;
}

function playTones(tones: Tone[]) {
  if (!enabled) return;
  void resume().then((ctx) => {
    if (!ctx || !master) return;
    const now = ctx.currentTime;
    for (const tone of tones) {
      const osc = ctx.createOscillator();
      const gain = ctx.createGain();
      const filter = ctx.createBiquadFilter();
      filter.type = 'lowpass';
      filter.frequency.value = 1800;
      filter.Q.value = 0.7;

      osc.type = tone.type ?? 'sine';
      osc.frequency.value = tone.frequency;

      const start = now + (tone.delay ?? 0);
      const attack = tone.attack ?? 0.012;
      const release = tone.release ?? Math.max(0.04, tone.duration * 0.55);
      const peak = tone.gain ?? 1;
      const end = start + tone.duration;

      gain.gain.setValueAtTime(0.0001, start);
      gain.gain.exponentialRampToValueAtTime(Math.max(0.0001, peak), start + attack);
      gain.gain.exponentialRampToValueAtTime(0.0001, end);

      osc.connect(filter);
      filter.connect(gain);
      gain.connect(master);
      osc.start(start);
      osc.stop(end + release);
    }
  });
}

export function setUiSoundEnabled(next: boolean) {
  enabled = next;
  if (master) master.gain.value = next ? MASTER_GAIN : 0;
  setAmbientEnabled(next);
}

export function isUiSoundUnlocked() {
  return unlocked;
}


export function unlockUiSound() {
  const ctx = ensureContext();
  if (!ctx) return;
  void ctx.resume().then(() => {
    unlocked = ctx.state === 'running';
  });
  if (enabled) primeAmbient(ctx);
}

export function playUiTap() {
  playTones([
    { frequency: 620, duration: 0.05, gain: 0.55, type: 'triangle' },
    { frequency: 880, duration: 0.04, gain: 0.28, delay: 0.02, type: 'sine' },
  ]);
}

export function playUiBoosterOpen() {
  playTones([
    { frequency: 240, duration: 0.12, gain: 0.45, type: 'sine' },
    { frequency: 360, duration: 0.14, gain: 0.35, delay: 0.05, type: 'triangle' },
    { frequency: 520, duration: 0.16, gain: 0.25, delay: 0.1, type: 'sine' },
  ]);
}

export function playUiCardFlip() {
  playTones([
    { frequency: 480, duration: 0.07, gain: 0.4, type: 'triangle' },
    { frequency: 720, duration: 0.09, gain: 0.32, delay: 0.03, type: 'sine' },
  ]);
}

const RARITY_STING: Record<Rarity, Tone[]> = {
  common: [{ frequency: 392, duration: 0.08, gain: 0.28, type: 'sine' }],
  uncommon: [
    { frequency: 440, duration: 0.08, gain: 0.3, type: 'sine' },
    { frequency: 554, duration: 0.1, gain: 0.22, delay: 0.04, type: 'sine' },
  ],
  rare: [
    { frequency: 494, duration: 0.09, gain: 0.32, type: 'triangle' },
    { frequency: 659, duration: 0.12, gain: 0.26, delay: 0.05, type: 'sine' },
  ],
  epic: [
    { frequency: 523, duration: 0.1, gain: 0.34, type: 'triangle' },
    { frequency: 698, duration: 0.12, gain: 0.28, delay: 0.05, type: 'sine' },
    { frequency: 880, duration: 0.14, gain: 0.2, delay: 0.1, type: 'sine' },
  ],
  legendary: [
    { frequency: 392, duration: 0.16, gain: 0.32, type: 'triangle' },
    { frequency: 494, duration: 0.16, gain: 0.28, delay: 0.08, type: 'triangle' },
    { frequency: 587, duration: 0.24, gain: 0.3, delay: 0.16, type: 'triangle' },
    { frequency: 784, duration: 0.3, gain: 0.22, delay: 0.24, type: 'sine' },
    { frequency: 1175, duration: 0.12, gain: 0.1, delay: 0.36, type: 'sine' },
    { frequency: 1568, duration: 0.1, gain: 0.07, delay: 0.44, type: 'sine' },
  ],
  mythic: [
    { frequency: 110, duration: 0.55, gain: 0.3, type: 'sine', attack: 0.02, release: 0.32 },
    { frequency: 116, duration: 0.55, gain: 0.18, delay: 0.01, type: 'sine', release: 0.32 },
    { frequency: 220, duration: 0.36, gain: 0.16, delay: 0.08, type: 'triangle' },
    { frequency: 415, duration: 0.22, gain: 0.14, delay: 0.18, type: 'sawtooth' },
    { frequency: 622, duration: 0.18, gain: 0.1, delay: 0.28, type: 'triangle' },
    { frequency: 932, duration: 0.16, gain: 0.08, delay: 0.36, type: 'sine' },
  ],
  divine: [
    { frequency: 196, duration: 0.95, gain: 0.22, type: 'sine', attack: 0.18, release: 0.5 },
    { frequency: 294, duration: 1.05, gain: 0.18, delay: 0.08, type: 'sine', attack: 0.2, release: 0.55 },
    { frequency: 392, duration: 1.1, gain: 0.16, delay: 0.16, type: 'sine', attack: 0.22, release: 0.6 },
    { frequency: 587, duration: 1.15, gain: 0.12, delay: 0.28, type: 'triangle', attack: 0.2, release: 0.6 },
    { frequency: 784, duration: 0.7, gain: 0.1, delay: 0.72, type: 'sine', attack: 0.02, release: 0.5 },
    { frequency: 1175, duration: 0.55, gain: 0.08, delay: 0.8, type: 'sine', attack: 0.01, release: 0.45 },
  ],
  senita: [
    { frequency: 73, duration: 1.8, gain: 0.22, type: 'sine', attack: 0.4, release: 0.9 },
    { frequency: 110, duration: 2.1, gain: 0.18, delay: 0.18, type: 'sine', attack: 0.45, release: 1 },
    { frequency: 165, duration: 2.2, gain: 0.15, delay: 0.34, type: 'triangle', attack: 0.48, release: 1 },
    { frequency: 220, duration: 2.3, gain: 0.13, delay: 0.52, type: 'sine', attack: 0.5, release: 1.1 },
    { frequency: 330, duration: 1.9, gain: 0.1, delay: 0.78, type: 'triangle', attack: 0.3, release: 0.9 },
    { frequency: 440, duration: 1.6, gain: 0.08, delay: 1.05, type: 'sine', attack: 0.25, release: 0.8 },
    { frequency: 659, duration: 1.2, gain: 0.055, delay: 1.3, type: 'sine', attack: 0.18, release: 0.7 },
  ],
};

const RARITY_TEASE: Partial<Record<Rarity, Tone[]>> = {
  legendary: [
    { frequency: 392, duration: 0.2, gain: 0.18, type: 'sine' },
    { frequency: 494, duration: 0.22, gain: 0.16, delay: 0.12, type: 'sine' },
    { frequency: 587, duration: 0.3, gain: 0.14, delay: 0.24, type: 'triangle' },
  ],
  mythic: [
    { frequency: 98, duration: 0.42, gain: 0.26, type: 'sine', attack: 0.04, release: 0.2 },
    { frequency: 104, duration: 0.42, gain: 0.14, delay: 0.02, type: 'sine' },
    { frequency: 196, duration: 0.32, gain: 0.12, delay: 0.16, type: 'triangle' },
  ],
  divine: [
    { frequency: 262, duration: 0.75, gain: 0.15, type: 'sine', attack: 0.22, release: 0.4 },
    { frequency: 392, duration: 0.85, gain: 0.11, delay: 0.12, type: 'sine', attack: 0.26, release: 0.45 },
  ],
  senita: [
    { frequency: 55, duration: 1.2, gain: 0.3, type: 'sine', attack: 0.3, release: 0.6 },
    { frequency: 58, duration: 1.2, gain: 0.18, type: 'sine', attack: 0.3, release: 0.6 },
    { frequency: 233, duration: 0.9, gain: 0.07, delay: 0.3, type: 'sawtooth', attack: 0.4, release: 0.5 },
  ],
};

export function playUiRaritySting(rarity: Rarity) {
  playTones(RARITY_STING[rarity] ?? RARITY_STING.common);
}


export function playUiRarityTease(rarity: Rarity) {
  const tones = RARITY_TEASE[rarity];
  if (tones) playTones(tones);
}

let noiseBuffer: AudioBuffer | null = null;

function noise(ctx: AudioContext): AudioBuffer {
  if (noiseBuffer && noiseBuffer.sampleRate === ctx.sampleRate) return noiseBuffer;
  const buffer = ctx.createBuffer(1, ctx.sampleRate * 2, ctx.sampleRate);
  const data = buffer.getChannelData(0);
  for (let i = 0; i < data.length; i += 1) data[i] = Math.random() * 2 - 1;
  noiseBuffer = buffer;
  return buffer;
}

type Scheduled = AudioScheduledSourceNode;


function envelope(param: AudioParam, start: number, attack: number, peak: number, end: number) {
  param.setValueAtTime(0.0001, start);
  param.exponentialRampToValueAtTime(Math.max(0.0001, peak), start + attack);
  param.exponentialRampToValueAtTime(0.0001, end);
}

type Voices = ReturnType<typeof voices>;


function voices(ctx: AudioContext, bus: AudioNode, sources: Scheduled[]) {
  const osc = (type: OscillatorType, from: number, to: number, start: number, end: number, peak: number, attack: number, target: AudioNode = bus) => {
    const node = ctx.createOscillator();
    const gain = ctx.createGain();
    node.type = type;
    node.frequency.setValueAtTime(from, start);
    if (to !== from) node.frequency.exponentialRampToValueAtTime(to, end);
    envelope(gain.gain, start, attack, peak, end);
    node.connect(gain);
    gain.connect(target);
    node.start(start);
    node.stop(end + 0.05);
    sources.push(node);
  };

  const hiss = (filterType: BiquadFilterType, freqFrom: number, freqTo: number, start: number, end: number, peak: number, attack: number) => {
    const src = ctx.createBufferSource();
    src.buffer = noise(ctx);
    src.loop = true;
    const filter = ctx.createBiquadFilter();
    filter.type = filterType;
    filter.frequency.setValueAtTime(freqFrom, start);
    filter.frequency.exponentialRampToValueAtTime(freqTo, end);
    const gain = ctx.createGain();
    envelope(gain.gain, start, attack, peak, end);
    src.connect(filter);
    filter.connect(gain);
    gain.connect(bus);
    src.start(start);
    src.stop(end + 0.05);
    sources.push(src);
  };

  return { osc, hiss };
}

function senitaGraph(ctx: AudioContext, bus: AudioNode, t0: number, impactOnly: boolean): Scheduled[] {
  const sources: Scheduled[] = [];
  const { osc, hiss }: Voices = voices(ctx, bus, sources);

  const thump = (at: number, peak: number) => {
    osc('sine', 90, 32, at, at + 0.42, peak, 0.006);
    osc('sine', 90, 32, at + 0.2, at + 0.55, peak * 0.7, 0.006);
  };

  const impact = (at: number) => {
    osc('sine', 72, 22, at, at + 2.2, 1.6, 0.004);
    osc('triangle', 140, 40, at, at + 0.9, 0.7, 0.004);
    hiss('lowpass', 4200, 180, at, at + 1.8, 1.1, 0.004);
    hiss('bandpass', 2800, 900, at, at + 0.7, 0.5, 0.002);
    const chord = [146.83, 174.61, 220, 233.08, 293.66, 349.23];
    chord.forEach((freq, index) => {
      osc('sawtooth', freq, freq, at + 0.08, at + 2.9, 0.075, 0.35);
      osc('sawtooth', freq * 1.006, freq * 1.006, at + 0.08 + index * 0.01, at + 2.9, 0.05, 0.4);
    });
    [1174.66, 1760, 2349.32].forEach((freq, index) => {
      osc('sine', freq, freq, at + 0.95 + index * 0.08, at + 2.4, 0.06, 0.01);
    });
  };

  if (impactOnly) {
    impact(t0);
    return sources;
  }

  // Descent: sub drone, rumble and a rising tear, punctuated by heartbeats.
  osc('sine', 41, 41, t0, t0 + 3.3, 0.9, 1.4);
  osc('sine', 55, 52, t0 + 0.3, t0 + 3.3, 0.5, 1.2);
  hiss('lowpass', 110, 520, t0, t0 + 3.3, 0.8, 1.6);
  osc('sawtooth', 70, 880, t0 + 0.5, t0 + 3.22, 0.16, 2.4);
  osc('sawtooth', 72, 905, t0 + 0.5, t0 + 3.22, 0.12, 2.4);
  hiss('highpass', 300, 6000, t0 + 1.4, t0 + 3.22, 0.35, 1.6);
  [0.55, 1.25, 1.9, 2.4, 2.8].forEach((at, index) => thump(t0 + at, 0.7 + index * 0.12));
  // Corruption stutter on the screen shake.
  const stutter: [number, number][] = [
    [1.9, 1320],
    [1.97, 210],
    [2.05, 2480],
    [2.46, 660],
    [2.52, 3100],
  ];
  for (const [at, freq] of stutter) {
    osc('square', freq, freq * 0.6, t0 + at, t0 + at + 0.05, 0.12, 0.002);
  }
  impact(t0 + 3.25);
  return sources;
}


function mythicGraph(ctx: AudioContext, bus: AudioNode, t0: number): Scheduled[] {
  const sources: Scheduled[] = [];
  const { osc, hiss } = voices(ctx, bus, sources);
  const shatter = t0 + 0.78;

  osc('sine', 52, 44, t0, shatter + 0.05, 0.6, 0.25);
  osc('sawtooth', 110, 640, t0 + 0.08, shatter, 0.07, 0.6);
  osc('sawtooth', 113, 655, t0 + 0.08, shatter, 0.05, 0.6);
  [0.04, 0.2, 0.33, 0.46, 0.58, 0.68].forEach((at, index) => {
    hiss('bandpass', 4200 - index * 300, 2600, t0 + at, t0 + at + 0.05, 0.5 + index * 0.06, 0.002);
    osc('square', 1800 + index * 240, 900, t0 + at, t0 + at + 0.03, 0.05, 0.002);
  });

  osc('sine', 96, 26, shatter, shatter + 0.75, 1.5, 0.004);
  hiss('highpass', 2400, 7600, shatter, shatter + 0.75, 1.1, 0.003);
  hiss('bandpass', 5200, 3000, shatter + 0.02, shatter + 0.4, 0.6, 0.002);
  [3520, 4186, 2794, 5274, 3951, 4699, 3136, 5920].forEach((freq, index) => {
    const at = shatter + 0.03 + index * 0.055 + (index % 3) * 0.02;
    osc('sine', freq, freq * 0.985, at, at + 0.22 + (index % 3) * 0.08, 0.09, 0.002);
  });
  [130.81, 155.56, 196, 233.08].forEach((freq, index) => {
    osc('triangle', freq, freq, shatter + 0.12, shatter + 1.9, 0.09 - index * 0.012, 0.25);
    osc('triangle', freq * 1.012, freq * 1.012, shatter + 0.14, shatter + 1.9, 0.05, 0.3);
  });
  return sources;
}


function divineGraph(ctx: AudioContext, bus: AudioNode, t0: number): Scheduled[] {
  const sources: Scheduled[] = [];
  const { osc, hiss } = voices(ctx, bus, sources);
  const reveal = t0 + 1.85;

  [146.83, 220, 293.66, 369.99, 440].forEach((freq, index) => {
    osc('sine', freq, freq, t0 + index * 0.06, reveal + 0.1, 0.16 - index * 0.015, 1.1);
    osc('triangle', freq * 1.004, freq * 1.004, t0 + 0.1 + index * 0.06, reveal + 0.1, 0.06, 1.2);
  });
  hiss('bandpass', 700, 3200, t0 + 0.2, reveal, 0.35, 1.4);
  [1174.66, 1479.98, 1760, 2349.32, 1760, 2959.96].forEach((freq, index) => {
    const at = t0 + 0.5 + index * 0.15;
    osc('sine', freq, freq, at, at + 0.7, 0.05, 0.01);
  });

  osc('sine', 98, 49, reveal, reveal + 1.6, 0.9, 0.01);
  hiss('lowpass', 6000, 400, reveal, reveal + 1.4, 0.5, 0.005);
  [1, 2.76, 5.4].forEach((ratio, index) => {
    osc('sine', 784 * ratio, 784 * ratio, reveal, reveal + 2.6 - index * 0.6, 0.22 / (index + 1), 0.004);
  });
  [196, 293.66, 392, 493.88, 587.33, 783.99].forEach((freq, index) => {
    osc('sine', freq, freq, reveal + 0.02 + index * 0.02, reveal + 3.2, 0.13 - index * 0.012, 0.18);
    osc('triangle', freq * 1.005, freq * 1.005, reveal + 0.05, reveal + 3.2, 0.045, 0.25);
  });
  return sources;
}

type Graph = (ctx: AudioContext, bus: AudioNode, t0: number) => Scheduled[];

function runScore(graph: Graph): () => void {
  if (!enabled) return () => undefined;
  let stopped = false;
  let teardown: (() => void) | null = null;
  void resume().then((ctx) => {
    if (!ctx || !master || stopped) return;
    const bus = ctx.createGain();
    const compressor = ctx.createDynamicsCompressor();
    compressor.threshold.value = -14;
    compressor.ratio.value = 6;
    bus.gain.value = 1.4;
    bus.connect(compressor);
    compressor.connect(master);
    const sources = graph(ctx, bus, ctx.currentTime + 0.02);
    teardown = () => {
      const now = ctx.currentTime;
      bus.gain.cancelScheduledValues(now);
      bus.gain.setValueAtTime(bus.gain.value, now);
      bus.gain.linearRampToValueAtTime(0, now + 0.18);
      for (const source of sources) {
        try {
          source.stop(now + 0.2);
        } catch {
          // Already stopped.
        }
      }
      window.setTimeout(() => compressor.disconnect(), 400);
    };
  });
  return () => {
    stopped = true;
    teardown?.();
  };
}


export function playUiSenitaCinematic(): () => void {
  return runScore((ctx, bus, t0) => senitaGraph(ctx, bus, t0, false));
}


export function playUiMythicCinematic(): () => void {
  return runScore(mythicGraph);
}


export function playUiDivineCinematic(): () => void {
  return runScore(divineGraph);
}


export function playUiSenitaImpact(): () => void {
  return runScore((ctx, bus, t0) => senitaGraph(ctx, bus, t0, true));
}
